// Opt-in (Settings → Upvalue Settings): after an upvalue lands, drop a toast with the
// wallet's remaining balance. A burst of quick upvalues settles into a single toast.
export const UPVALUE_BALANCE_TOAST_KIND = 'upvalue-balance'
export const UPVALUE_BALANCE_TOAST_SETTLE_MS = 600
export const UPVALUE_BALANCE_TOAST_DURATION_MS = 3000

export const upvalueBalanceToastEnabled = (
    spaceOptions: { upvalueBalanceToast?: boolean } | null | undefined,
) => spaceOptions?.upvalueBalanceToast === true

export type BalanceNoticeVariant = 'bitcoin' | 'usd'

type BalanceToastDeps = {
    isEnabled: () => boolean
    // Remaining spendable sats, or null when the wallet balance isn't known.
    readBalance: () => number | null
    formatBalance: (sats: number) => string
    // Toast style for the unit the balance is shown in (the user's money color).
    noticeVariant: (sats: number) => BalanceNoticeVariant
    // The visible toast, if any: `null` when nothing is showing.
    currentNotice: () => { kind?: string } | null
    showNotice: (
        message: string,
        options: { kind: string; durationMs: number; variant: BalanceNoticeVariant },
    ) => void
    // Patches the visible notice in place so the drop-in animation doesn't replay.
    updateNotice: (
        kind: string,
        message: string,
        options: { durationMs: number; variant: BalanceNoticeVariant },
    ) => void
}

export const createUpvalueBalanceToast = (deps: BalanceToastDeps) => {
    let settleTimer: ReturnType<typeof setTimeout> | null = null

    const show = () => {
        if (!deps.isEnabled()) return
        const balance = deps.readBalance()
        if (balance == null) return

        const sats = Math.max(0, balance)
        const message = `Balance: ${deps.formatBalance(sats)}`
        const options = {
            durationMs: UPVALUE_BALANCE_TOAST_DURATION_MS,
            variant: deps.noticeVariant(sats),
        }
        const current = deps.currentNotice()
        if (!current) {
            deps.showNotice(message, { kind: UPVALUE_BALANCE_TOAST_KIND, ...options })
        } else if (current.kind === UPVALUE_BALANCE_TOAST_KIND) {
            deps.updateNotice(UPVALUE_BALANCE_TOAST_KIND, message, options)
        }
        // Another toast is up (an error, an in-flight payment): leave it alone.
    }

    return () => {
        if (!deps.isEnabled()) return
        if (settleTimer) clearTimeout(settleTimer)
        settleTimer = setTimeout(() => {
            settleTimer = null
            show()
        }, UPVALUE_BALANCE_TOAST_SETTLE_MS)
    }
}
